import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useAuthContext } from '../../../context/AuthContext';

const GuestSignupModal = ({ 
  open, 
  onClose, 
  onSignup, 
  onContinueGuest, 
  onSkip,
  userScore = 0,
  guestName = 'Guest'
}) => {
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const { migrateGuestToRegistered } = useAuthContext();

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fname) newErrors.fname = 'First name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setAlertMessage('');

    try {
      const response = await migrateGuestToRegistered({
        fname: formData.fname,
        lname: formData.lname,
        email: formData.email,
        password: formData.password,
      });

      if (response.success) {
        onSignup(response.user);
        onClose();
      }
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      fname: '',
      lname: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
    setErrors({});
    setAlertMessage('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h4" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎉 Great Score!
          <Chip 
            icon={<StarIcon />} 
            label={`${userScore.toLocaleString()} Points`} 
            color="primary" 
            variant="outlined"
          />
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
          You've completed the quiz as a guest! Create an account to save your progress and join the leaderboard.
        </Typography>

        {alertMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {alertMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSignup} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Create Account to Save Progress
          </Typography>
          
          <TextField
            fullWidth
            label="First Name"
            value={formData.fname}
            onChange={handleInputChange('fname')}
            error={!!errors.fname}
            helperText={errors.fname}
            margin="normal"
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lname}
            onChange={handleInputChange('lname')}
            error={!!errors.lname}
            helperText={errors.lname}
            margin="normal"
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!errors.password}
            helperText={errors.password}
            margin="normal"
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            margin="normal"
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{ mt: 2, py: 1.5 }}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Save Progress & Join Leaderboard'
            )}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onContinueGuest}
            sx={{ py: 1.5 }}
          >
            Continue as Guest ({guestName})
          </Button>
          
          <Button
            variant="text"
            fullWidth
            onClick={onSkip}
            sx={{ py: 1.5 }}
          >
            Just View Results
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
          By creating an account, you can save your progress, compete on leaderboards, and unlock more features!
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default GuestSignupModal;
